import { useCallback, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { API } from '@trinity/chat';
import {
  QuantAnalysisAPIProvider,
  QuantAnalysisItemsContext,
  QuantAnalysisContext,
  QuantAnalysisLoadingContext,
  QuantAnalysisVersionContext,
  QuantAnalysisVersionsContext,
} from '@trinity/chat-components';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { format } from 'date-fns';
import type { IDropDownItem } from '@/components/presentation/Dropdown';
import { Dropdown } from '@/components/presentation/Dropdown';
import { QuantAnalysisSearch } from '@/components/Project.QuantAnalysis';
import { useSelectedQuantAnalysisItem } from '@trinity/chat-components';
import { QuantAnalysisFilterContainer } from '@trinity/chat-components/containers/QuantAnalysis/QuantAnalysisFilterContainer.js';
import { ProjectQuantAnalysisContainer } from '@trinity/chat-components/containers/QuantAnalysis/ProjectQuantAnalysis.js'
import { QuantAnalysisActions } from './Actions';
import { AnalysisContent } from './Analysis.Content';
import { QuantAnalysisLayout } from './QuantAnalysisLayout';
import styles from './style/QuantAnalysis.module.css';

interface VersionDropdownItem extends IDropDownItem {
  id: number;
  name: string;
  versionNumber: number;
  createdOn?: string;
}

const ProjectQuantAnalysis = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const isLoading = useContext(QuantAnalysisLoadingContext);
  const selectedAnalysisItem = useSelectedQuantAnalysisItem();

  // Define options with the correct type
  const quantAnalysis = useContext(QuantAnalysisContext);
  const items = useContext(QuantAnalysisItemsContext);
  const version = useContext(QuantAnalysisVersionContext);
  const versions = useContext(QuantAnalysisVersionsContext);

  const changeSelectedVersion = useCallback((versionNumber: number) => {
    if (version && version.versionNumber === versionNumber) {
      return;
    }

    if (!quantAnalysis) {
      console.log('quantAnalysis is null, cannot navigate');
      return;
    }

    navigate(`/projects/${projectId}/quantanalysis/${quantAnalysis.id}/${versionNumber}`);
  }, [version, navigate, projectId, quantAnalysis]);

  if (isLoading || !quantAnalysis) {
    return (
      <QuantAnalysisLayout
        isLoading={true}
        header={null}
        sidebar={null}
        content={null}
      />
    );
  }

  if (isLoading) {
    return (
      <QuantAnalysisLayout
        isLoading={true}
        header={null}
        sidebar={null}
        content={null}
      />
    );
  }

  const headerContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      Analyses
      {versions && version && (
        <div style={{ width: '60px' }}>
          <Dropdown<VersionDropdownItem>
            sameWidth={false}
            items={versions.map(v => {
              // Handle both snake_case and camelCase field names with proper typing
              const versionData = v as { created_on?: string; createdOn?: string; versionNumber: number };
              const createdDate = versionData.created_on || versionData.createdOn;

              return {
                id: v.versionNumber,
                name: v.name,
                versionNumber: v.versionNumber,
                createdOn: createdDate,
              };
            })}
            text={`v${version.versionNumber}`}
            getItemValue={(v: VersionDropdownItem) => (
              <>
                Version {v.versionNumber}&nbsp;
                <span className={styles.versionSelectOptionDate}>
                  {v.createdOn ? format(new Date(v.createdOn), 'M/d/yyyy') : 'No date'}
                </span>
              </>
            )}
            onSelect={(v: VersionDropdownItem) => changeSelectedVersion(v.versionNumber)}
          />
        </div>
      )}
    </div>
  );

  const actions = <QuantAnalysisActions />;

  const sidebar = (
    <QuantAnalysisSearch options={items} />
  );

  const content = (
    <QuantAnalysisFilterContainer key={`quant-filter-${selectedAnalysisItem?.id}`}>
      {selectedAnalysisItem && (
        <AnalysisContent
          className={styles.content}
          value={selectedAnalysisItem} />
      )}
    </QuantAnalysisFilterContainer>
  );

  return (
    <QuantAnalysisLayout
      isLoading={false}
      header={headerContent}
      actions={actions}
      sidebar={sidebar}
      content={content}
    />
  );
};

type RouterParams = {
  projectId: string;
  quantAnalysisId: string;
  versionNumber: string;
};

const WithState = () => {
  const params = useParams<RouterParams>();
  const apiClient = useChatApiClient();

  const quantAnalysisId = useMemo(() => {
    return params.quantAnalysisId ? +params.quantAnalysisId : undefined;
  }, [params.quantAnalysisId]);

  const versionNumber = useMemo(() => {
    return params.versionNumber ? +params.versionNumber : undefined;
  }, [params.versionNumber]);

  const projectId = useMemo(() => {
    return params.projectId ? +params.projectId : undefined;
  }, [params.projectId]);

  const quantAnalysisAPI = useMemo(() => ({
    getProjectQuantAnalysis: async (data: API.ProjectsQuantAnalysis.GetProjectQuantAnalysis.Request) => {
      const response = await apiClient.quantAnalysis.getProjectQuantAnalysis(data);
      return response.data;
    },
    getProjectQuantAnalysisVersions: async (data: API.ProjectsQuantAnalysis.GetProjectQuantAnalysisVersions.Request) => {
      const response = await apiClient.quantAnalysis.getProjectQuantAnalysisVersions(data);
      return response.data;
    },
  }), [apiClient]);

  if (!projectId) {
    return <div>Project not found</div>;
  }

  return (
    <QuantAnalysisAPIProvider value={quantAnalysisAPI}>
      <ProjectQuantAnalysisContainer
        projectId={projectId}
        quantAnalysisId={quantAnalysisId}
        versionNumber={versionNumber}
      >
        <ProjectQuantAnalysis />
      </ProjectQuantAnalysisContainer>
    </QuantAnalysisAPIProvider>
  );
};

export { WithState as QuantAnalysis };
